import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Button, 
  Typography, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper, 
  TextField, 
  IconButton, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Chip,
  Tooltip,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  Divider,
  Breadcrumbs,
  Link as MuiLink,
  Avatar,
  MenuItem,
  Snackbar,
  Alert
} from '@mui/material';
import {
  Plus,
  Edit,
  Trash2,
  ChevronRight,
  UserPlus,
  RefreshCw,
  Mail,
  Briefcase,
  Search,
  Download,
  Upload,
  ExternalLink,
  ArrowLeft,
  History,
  Milestone,
  CalendarClock,
  Save
} from 'lucide-react';
import axios from 'axios';
import { useParams, Link, useNavigate } from 'react-router-dom';
import API_BASE_URL from '../../Config/config';
import {
  ProjectStatusChip,
  ProjectStatusTimeline,
  StatusSelectPopover,
  StatusConfirmDialog,
  StatusHistoryDialog,
  patchVersionStatus,
  getAdminIdentity,
  normalizeStatus,
  adminIdentityParams
} from './projectStatus';

// ── Version scheduling (Dynamic Version Enrollment) ─────────────────────────
// Field order matches the enrollment lifecycle. All optional; timezone-aware:
// datetime-local inputs are entered in the admin's local timezone and stored
// as UTC ISO strings (TIMESTAMPTZ on the backend).
const SCHEDULE_FIELDS = [
  { key: 'enrollment_opens', label: 'Enrollment Opens' },
  { key: 'enrollment_closes', label: 'Enrollment Closes' },
  { key: 'start_date', label: 'Start Date' },
  { key: 'end_date', label: 'End Date' },
  { key: 'sprint_start_date', label: 'Expected Sprint Start' },
  { key: 'sprint_end_date', label: 'Expected Sprint End' },
  { key: 'kickoff_date', label: 'Project Kickoff Date' },
  { key: 'completion_date', label: 'Version Completion Date' }
];

// ISO (UTC) → value for <input type="datetime-local"> in the LOCAL timezone
const isoToLocalInput = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

// datetime-local value (local timezone) → UTC ISO string, '' → null (clears)
const localInputToIso = (value) => {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
};

// Client-side mirror of the server's schedule rules — same messages, so the
// admin sees identical errors with or without a round-trip.
const validateScheduleForm = (form) => {
  const errors = {};
  const t = {};
  SCHEDULE_FIELDS.forEach(({ key }) => {
    t[key] = form[key] ? new Date(form[key]).getTime() : null;
  });
  if (t.start_date != null && t.end_date != null && t.start_date >= t.end_date) {
    errors.end_date = 'End Date must be after Start Date';
  }
  if (t.enrollment_closes != null && t.start_date != null && t.enrollment_closes > t.start_date) {
    errors.enrollment_closes = 'Enrollment Close Date cannot exceed Start Date';
  }
  if (t.enrollment_opens != null && t.enrollment_closes != null && t.enrollment_opens >= t.enrollment_closes) {
    errors.enrollment_opens = 'Enrollment Opens must be before Enrollment Closes';
  }
  if (t.sprint_start_date != null && t.sprint_end_date != null && t.sprint_start_date >= t.sprint_end_date) {
    errors.sprint_end_date = 'Expected Sprint End must be after Expected Sprint Start';
  }
  if (form.max_members !== '' && form.max_members != null) {
    const n = parseInt(form.max_members, 10);
    if (!Number.isFinite(n) || n < 1) errors.max_members = 'Maximum Members must be a positive number';
  }
  return errors;
};

const VersionDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [teamMembers, setTeamMembers] = useState([]);
  const [openAddMember, setOpenAddMember] = useState(false);
  const [availableRoles, setAvailableRoles] = useState([]);
  const [allMembers, setAllMembers] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [statusPopoverAnchor, setStatusPopoverAnchor] = useState(null);
  const [pendingStatus, setPendingStatus] = useState(null);
  const [savingStatus, setSavingStatus] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [syncing, setSyncing] = useState(false);

  // Schedule & Enrollment Window editor
  const [scheduleForm, setScheduleForm] = useState(null); // null until version loads
  const [scheduleErrors, setScheduleErrors] = useState({});
  const [savingSchedule, setSavingSchedule] = useState(false);

  // RBAC — status editable only for Admin / Super Admin
  const canEditStatus = getAdminIdentity().isAdmin;
  const [memberForm, setMemberForm] = useState({
    member_id: '',
    role_id: '',
    allocation_percentage: 100,
    sprint_assignment: ''
  });

  useEffect(() => {
    fetchVersionDetails();
    fetchRoles();
    fetchAllMembers();
  }, [id]);

  const fetchVersionDetails = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_BASE_URL}/api/nextgen-builder/versions/${id}`);
      if (res.data.success) {
        setData(res.data.data);
        // Seed the schedule editor from the fetched version (local timezone)
        const v = res.data.data?.version || {};
        const seeded = {};
        SCHEDULE_FIELDS.forEach(({ key }) => { seeded[key] = isoToLocalInput(v[key]); });
        seeded.max_members = v.max_members != null ? String(v.max_members) : '';
        setScheduleForm(seeded);
        setScheduleErrors({});
      }
    } catch (err) {
      console.error('Failed to fetch version details', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchRoles = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/nextgen-builder/roles`);
      if (res.data.success) setAvailableRoles(res.data.data);
    } catch (err) { console.error(err); }
  };

  const fetchAllMembers = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/nextgen-builder/members`);
      if (res.data.success) setAllMembers(res.data.data);
    } catch (err) { console.error(err); }
  };

  const handleAddMember = async () => {
    try {
      const res = await axios.post(`${API_BASE_URL}/api/nextgen-builder/version-team`, {
        ...memberForm,
        version_id: id
      });
      if (res.data.success) {
        setOpenAddMember(false);
        fetchVersionDetails();
        setSnackbar({ open: true, message: 'Member added successfully', severity: 'success' });
      } else {
        setSnackbar({ open: true, message: res.data.message || 'Failed to add member', severity: 'error' });
      }
    } catch (err) {
      console.error(err);
      let errorMsg = 'Error adding member';
      if (err.response?.data) {
        errorMsg = err.response.data.message || err.response.data.error || errorMsg;
      }
      setSnackbar({ open: true, message: errorMsg, severity: 'error' });
    }
  };

  const handleRemoveMember = async (vtmId) => {
    if (!window.confirm('Are you sure you want to remove this member?')) return;
    try {
      const res = await axios.delete(`${API_BASE_URL}/api/nextgen-builder/version-team/${vtmId}`);
      if (res.data.success) {
        fetchVersionDetails();
        setSnackbar({ open: true, message: 'Member removed successfully', severity: 'success' });
      }
    } catch (err) {
      console.error(err);
      let errorMsg = 'Error removing member';
      if (err.response?.data) {
        errorMsg = err.response.data.message || err.response.data.error || errorMsg;
      }
      setSnackbar({ open: true, message: errorMsg, severity: 'error' });
    }
  };

  const handleStatusSelected = (newStatus) => {
    setStatusPopoverAnchor(null);
    if (!data?.version || normalizeStatus(data.version.status) === newStatus) return;
    setPendingStatus(newStatus);
  };

  const handleConfirmStatus = async () => {
    if (!pendingStatus || savingStatus) return; // duplicate-request guard
    try {
      setSavingStatus(true);
      const res = await patchVersionStatus(id, pendingStatus);
      if (res.data.success) {
        await fetchVersionDetails(); // reflects immediately — no page refresh
        const wf = res.data.data?.workflow;
        setSnackbar({
          open: true,
          message: wf?.nextVersionOpened
            ? `${res.data.message} ${wf.nextVersionOpened.version} enrollment is now OPEN.`
            : res.data.message,
          severity: 'success'
        });
        setPendingStatus(null);
      } else {
        setSnackbar({ open: true, message: res.data.message || 'Status update failed', severity: 'error' });
      }
    } catch (err) {
      const code = err.response?.status;
      const msg = err.response?.data?.message
        || (code === 403 ? 'Permission denied — Admin access required'
          : code === 404 ? 'Project version not found'
          : code === 422 ? 'Invalid status value'
          : err.message === 'Network Error' ? 'Network failure — please check your connection'
          : 'Status update failed');
      setSnackbar({ open: true, message: msg, severity: 'error' });
    } finally {
      setSavingStatus(false);
    }
  };

  // Save the Schedule & Enrollment Window (validated client-side first; the
  // server enforces the identical rules and replies 422 with field errors).
  const handleSaveSchedule = async () => {
    if (!scheduleForm || savingSchedule) return;
    const errors = validateScheduleForm(scheduleForm);
    setScheduleErrors(errors);
    if (Object.keys(errors).length > 0) {
      setSnackbar({ open: true, message: 'Please fix the highlighted schedule fields', severity: 'error' });
      return;
    }
    try {
      setSavingSchedule(true);
      const payload = {};
      SCHEDULE_FIELDS.forEach(({ key }) => { payload[key] = localInputToIso(scheduleForm[key]); });
      payload.max_members = scheduleForm.max_members === '' ? null : parseInt(scheduleForm.max_members, 10);
      const res = await axios.put(`${API_BASE_URL}/api/nextgen-builder/versions/${id}`, payload);
      if (res.data.success) {
        await fetchVersionDetails();
        setSnackbar({ open: true, message: 'Schedule updated successfully', severity: 'success' });
      } else {
        setSnackbar({ open: true, message: res.data.message || 'Schedule update failed', severity: 'error' });
      }
    } catch (err) {
      const serverErrors = err.response?.data?.errors;
      if (Array.isArray(serverErrors) && serverErrors.length > 0 && serverErrors[0]?.field) {
        const mapped = {};
        serverErrors.forEach(e => { if (e.field) mapped[e.field] = e.message; });
        setScheduleErrors(mapped);
      }
      setSnackbar({
        open: true,
        message: err.response?.data?.message || 'Schedule update failed',
        severity: 'error'
      });
    } finally {
      setSavingSchedule(false);
    }
  };

  // Reconcile the engineering team from pending enrollments (idempotent).
  const handleSyncTeam = async () => {
    if (syncing) return;
    try {
      setSyncing(true);
      const res = await axios.post(
        `${API_BASE_URL}/api/nextgen-builder/versions/${id}/sync-team`,
        adminIdentityParams()
      );
      if (res.data.success) {
        await fetchVersionDetails();
        const skipped = res.data.data?.skipped || [];
        setSnackbar({
          open: true,
          message: skipped.length > 0
            ? `${res.data.message} — ${skipped.map(s => `${s.email}: ${s.reason}`).join('; ')}`
            : res.data.message,
          severity: skipped.length > 0 ? 'warning' : 'success'
        });
      } else {
        setSnackbar({ open: true, message: res.data.message || 'Team sync failed', severity: 'error' });
      }
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.message || 'Team sync failed', severity: 'error' });
    } finally {
      setSyncing(false);
    }
  };

  const handleSendGithubInvite = async (githubUsername) => {
    if (!githubUsername) {
      setSnackbar({ open: true, message: 'No GitHub username on this enrollment', severity: 'warning' });
      return;
    }
    try {
      const res = await axios.post(`${API_BASE_URL}/api/nextgen-builder/github/invite`, {
        username: githubUsername,
        projectId: data?.version?.project_id
      });
      if (res.data.success) {
        setSnackbar({ open: true, message: `GitHub invitation sent to @${githubUsername}`, severity: 'success' });
      } else {
        setSnackbar({ open: true, message: res.data.message || 'GitHub invite failed', severity: 'error' });
      }
    } catch (err) {
      console.error(err);
      setSnackbar({ open: true, message: err.response?.data?.message || 'GitHub invite failed', severity: 'error' });
    }
  };

  const handleSendInvite = async (member) => {
    if (!member.email) {
      setSnackbar({ open: true, message: 'No email associated with this member slot', severity: 'warning' });
      return;
    }
    try {
      const res = await axios.post(`${API_BASE_URL}/api/nextgen-builder/send-invite`, {
        email: member.email,
        name: member.full_name,
        projectName: version.project_name,
        versionName: version.name
      });
      if (res.data.success) {
        setSnackbar({ open: true, message: 'Collaboration mail sent successfully', severity: 'success' });
      }
    } catch (err) {
      console.error(err);
      setSnackbar({ open: true, message: 'Failed to send collaboration mail', severity: 'error' });
    }
  };

  if (loading) return <Box display="flex" justifyContent="center" p={10} sx={{ backgroundColor: '#f5f7fa', minHeight: '100vh' }}><CircularProgress /></Box>;
  if (!data) return <Box p={4} sx={{ backgroundColor: '#f5f7fa', minHeight: '100vh' }}><Typography>Version not found</Typography></Box>;

  const { version, team, pendingEnrollments } = data;

  return (
    <Box p={4} sx={{ backgroundColor: '#f8fafc', minHeight: '100vh' }}>
      <Box mb={4}>
        <Breadcrumbs separator={<ChevronRight size={14} />} sx={{ mb: 2 }}>
          <MuiLink component={Link} to="/nextgen/projects" underline="hover" color="inherit" sx={{ fontSize: '0.875rem' }}>Projects</MuiLink>
          <MuiLink component={Link} to={`/nextgen/projects/${version.project_id}`} underline="hover" color="inherit" sx={{ fontSize: '0.875rem' }}>{version.project_name}</MuiLink>
          <Typography color="text.primary" sx={{ fontSize: '0.875rem', fontWeight: 500 }}>{version.name}</Typography>
        </Breadcrumbs>

        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
          <Box display="flex" alignItems="center" gap={2}>
            <IconButton 
              onClick={() => navigate(`/nextgen/projects/${version.project_id}`)} 
              sx={{ 
                color: '#843FEF', 
                bgcolor: 'white', 
                boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                '&:hover': { bgcolor: '#f0eaff' }
              }}
            >
              <ArrowLeft size={24} />
            </IconButton>
            <Box>
              <Typography variant="h4" fontWeight="800" sx={{ color: '#1e293b', letterSpacing: '-0.02em' }}>
                {version.name}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Engineering Team Management for {version.project_name}
              </Typography>
            </Box>
          </Box>
          <Box display="flex" gap={1.5}>
            <Tooltip title="Assign enrolled students (Awaiting Assignment) into this version's team">
              <span>
                <Button
                  variant="outlined"
                  startIcon={syncing ? <CircularProgress size={16} /> : <RefreshCw size={18} />}
                  disabled={syncing}
                  onClick={handleSyncTeam}
                  sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 'bold' }}
                >
                  {syncing ? 'Syncing…' : 'Sync Team'}
                </Button>
              </span>
            </Tooltip>
            <Button 
              variant="contained" 
              startIcon={<UserPlus size={18} />} 
              onClick={() => setOpenAddMember(true)}
              sx={{ 
                bgcolor: '#843FEF', 
                '&:hover': { bgcolor: '#7035cc' }, 
                borderRadius: '10px', 
                textTransform: 'none', 
                fontWeight: 'bold',
                boxShadow: '0 4px 14px 0 rgba(132, 63, 239, 0.39)'
              }}
            >
              Add Member
            </Button>
          </Box>
        </Box>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Box display="flex" flexDirection="column" gap={3}>
            <Card elevation={0} sx={{ borderRadius: '16px', border: '1px solid #e2e8f0' }}>
              <CardContent sx={{ p: 0 }}>
                <Box p={3} display="flex" justifyContent="space-between" alignItems="center" borderBottom="1px solid #f1f5f9">
                  <Typography variant="h6" fontWeight="700" color="#1e293b">Engineering Team</Typography>
                  <Box display="flex" gap={1}>
                    <IconButton size="small" sx={{ bgcolor: '#f1f5f9' }}><Search size={16} /></IconButton>
                    <IconButton size="small" sx={{ bgcolor: '#f1f5f9' }}><Download size={16} /></IconButton>
                  </Box>
                </Box>
                
                <TableContainer>
                  <Table>
                    <TableHead sx={{ bgcolor: '#f8fafc' }}>
                      <TableRow>
                        <TableCell sx={{ fontWeight: '600', color: '#64748b', pl: 3 }}>MEMBER / ROLE</TableCell>
                        <TableCell sx={{ fontWeight: '600', color: '#64748b' }}>ALLOCATION</TableCell>
                        <TableCell sx={{ fontWeight: '600', color: '#64748b' }}>STATUS</TableCell>
                        <TableCell align="right" sx={{ fontWeight: '600', color: '#64748b', pr: 3 }}>ACTIONS</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {team.map((m) => (
                        <TableRow key={m.id} hover sx={{ '& td': { py: 2, borderBottom: '1px solid #f1f5f9' } }}>
                          <TableCell sx={{ pl: 3 }}>
                            <Box display="flex" alignItems="center" gap={2}>
                              <Avatar sx={{ bgcolor: m.full_name ? '#843FEF' : '#f1f5f9', color: m.full_name ? 'white' : '#cbd5e1', width: 40, height: 40, fontSize: '1rem', fontWeight: 'bold' }}>
                                {m.full_name ? m.full_name.charAt(0) : '?'}
                              </Avatar>
                              <Box>
                                <Typography fontWeight="700" color={m.full_name ? "#1e293b" : "#94a3b8"}>
                                  {m.full_name || 'Vacant Slot'}
                                </Typography>
                                <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: '#64748b', fontWeight: '500' }}>
                                  <Briefcase size={12} /> {m.role_name}
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Box display="flex" alignItems="center" gap={1}>
                              <Box sx={{ width: 60, height: 6, bgcolor: '#f1f5f9', borderRadius: 3, overflow: 'hidden' }}>
                                <Box sx={{ width: `${m.allocation_percentage}%`, height: '100%', bgcolor: '#843FEF' }} />
                              </Box>
                              <Typography variant="caption" fontWeight="700" color="#64748b">{m.allocation_percentage}%</Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={m.member_id ? 'Assigned' : 'Vacant'} 
                              size="small" 
                              sx={{ 
                                fontWeight: '700', 
                                fontSize: '0.65rem',
                                height: 20,
                                bgcolor: m.member_id ? '#dcfce7' : '#fef2f2', 
                                color: m.member_id ? '#166534' : '#991b1b',
                                borderRadius: '4px'
                              }} 
                            />
                          </TableCell>
                          <TableCell align="right" sx={{ pr: 3 }}>
                            <Box display="flex" justifyContent="flex-end" gap={0.5}>
                              {m.email && (
                                <Tooltip title="Send Invite">
                                  <IconButton size="small" color="primary" onClick={() => handleSendInvite(m)}>
                                    <Mail size={16} />
                                  </IconButton>
                                </Tooltip>
                              )}
                              <IconButton size="small"><Edit size={16} /></IconButton>
                              <IconButton size="small" color="error" onClick={() => handleRemoveMember(m.id)}><Trash2 size={16} /></IconButton>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                      {team.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={4} align="center" sx={{ py: 12 }}>
                            <Box display="flex" flexDirection="column" alignItems="center" gap={1}>
                              <Typography variant="h6" color="#94a3b8" fontWeight="600">Teams not generated yet</Typography>
                              <Typography variant="body2" color="#cbd5e1">
                                Enrolled members are already counted above — click "Sync Team" to allocate them into teams
                              </Typography>
                            </Box>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>

            {/* Pending Enrollments Section */}
            <Card elevation={0} sx={{ borderRadius: '16px', border: '1px solid #e2e8f0' }}>
              <CardContent sx={{ p: 0 }}>
                <Box p={3} borderBottom="1px solid #f1f5f9">
                  <Typography variant="h6" fontWeight="700" color="#1e293b">Enrolled Students (Awaiting Assignment)</Typography>
                  <Typography variant="caption" color="textSecondary">Students who paid and selected this project but aren't assigned to a version yet.</Typography>
                </Box>
                <TableContainer>
                  <Table>
                    <TableHead sx={{ bgcolor: '#f8fafc' }}>
                      <TableRow>
                        <TableCell sx={{ fontWeight: '600', color: '#64748b', pl: 3 }}>STUDENT</TableCell>
                        <TableCell sx={{ fontWeight: '600', color: '#64748b' }}>PREFERRED ROLE</TableCell>
                        <TableCell sx={{ fontWeight: '600', color: '#64748b' }}>GITHUB</TableCell>
                        <TableCell sx={{ fontWeight: '600', color: '#64748b' }}>PLAN</TableCell>
                        <TableCell align="right" sx={{ fontWeight: '600', color: '#64748b', pr: 3 }}>ENROLLED ON</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {pendingEnrollments?.map((pe) => (
                        <TableRow key={pe.id} hover sx={{ '& td': { py: 2, borderBottom: '1px solid #f1f5f9' } }}>
                          <TableCell sx={{ pl: 3 }}>
                            <Box display="flex" alignItems="center" gap={2}>
                              <Avatar sx={{ bgcolor: '#843FEF', color: 'white', width: 32, height: 32, fontSize: '0.875rem' }}>
                                {pe.full_name?.charAt(0)}
                              </Avatar>
                              <Box>
                                <Typography fontWeight="600" color="#1e293b">{pe.full_name}</Typography>
                                <Typography variant="caption" color="#64748b">{pe.email}</Typography>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip label={pe.preferred_role} size="small" sx={{ fontWeight: 600, fontSize: '0.75rem' }} />
                          </TableCell>
                          <TableCell>
                            {pe.github_username ? (
                              <Box display="flex" alignItems="center" gap={0.5}>
                                <MuiLink href={`https://github.com/${pe.github_username}`} target="_blank" underline="hover" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>
                                  @{pe.github_username}
                                </MuiLink>
                                <Tooltip title="Send GitHub repo invitation">
                                  <IconButton size="small" color="primary" onClick={() => handleSendGithubInvite(pe.github_username)}>
                                    <ExternalLink size={14} />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            ) : (
                              <Typography variant="caption" color="#94a3b8">Not provided</Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight="600" color="#475569">{pe.current_subscription || 'Free'}</Typography>
                          </TableCell>
                          <TableCell align="right" sx={{ pr: 3 }}>
                            <Typography variant="caption" color="#64748b">{new Date(pe.created_at).toLocaleDateString()}</Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                      {(!pendingEnrollments || pendingEnrollments.length === 0) && (
                        <TableRow>
                          <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                            <Typography variant="body2" color="#94a3b8">No pending enrollments for this project</Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Box>
        </Grid>

        <Grid item xs={12} md={4}>
          <Box display="flex" flexDirection="column" gap={3}>
            <Card elevation={0} sx={{ borderRadius: '16px', border: '1px solid #e2e8f0' }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" fontWeight="700" mb={3} color="#1e293b">Version Details</Typography>
                <Box display="flex" flexDirection="column" gap={2.5}>
                  <Box>
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                      <Typography variant="caption" color="#94a3b8" fontWeight="600" sx={{ textTransform: 'uppercase' }}>Status</Typography>
                      <Tooltip title="View status history">
                        <IconButton size="small" onClick={() => setHistoryOpen(true)} aria-label="View status history" sx={{ color: '#94a3b8' }}>
                          <History size={15} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                    <Box mt={0.5} display="flex" gap={1} flexWrap="wrap">
                      <ProjectStatusChip
                        status={version.status}
                        clickable={canEditStatus}
                        loading={savingStatus}
                        onClick={(e) => canEditStatus && setStatusPopoverAnchor(e.currentTarget)}
                      />
                      <Chip
                        label={version.enrollment_open ? 'Enrollment Open' : 'Enrollment Closed'}
                        size="small"
                        sx={{
                          bgcolor: version.enrollment_open ? '#dcfce7' : '#f1f5f9',
                          color: version.enrollment_open ? '#166534' : '#64748b',
                          fontWeight: '700'
                        }}
                      />
                    </Box>
                    {version.status_updated_by && (
                      <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block', mt: 0.75 }}>
                        Updated by <b>{version.status_updated_by}</b>
                        {version.status_updated_at ? ` on ${new Date(version.status_updated_at).toLocaleString()}` : ''}
                      </Typography>
                    )}
                  </Box>
                  <Box>
                    <Typography variant="caption" color="#94a3b8" fontWeight="600" sx={{ textTransform: 'uppercase' }}>Members Enrolled</Typography>
                    {(() => {
                      const cap = version.capacity || Math.min(version.team_size || 25, 25);
                      // Version MEMBERSHIP — students enrolled into this
                      // version, independent of team allocation
                      const enrolled = parseInt(version.members_enrolled, 10) || 0;
                      const full = enrolled >= cap;
                      return (
                        <Box display="flex" alignItems="center" gap={1.5} mt={0.5}>
                          <Typography variant="h6" fontWeight="800" color={full ? '#dc2626' : '#1e293b'}>
                            {enrolled} / {cap}
                          </Typography>
                          <Box sx={{ flex: 1, height: 8, bgcolor: '#f1f5f9', borderRadius: 4, overflow: 'hidden' }}>
                            <Box sx={{ width: `${Math.min((enrolled / cap) * 100, 100)}%`, height: '100%', bgcolor: full ? '#dc2626' : '#843FEF' }} />
                          </Box>
                        </Box>
                      );
                    })()}
                    {!version.enrollment_open && (parseInt(version.members_enrolled, 10) || 0) >= (version.capacity || 25) && (
                      <Typography variant="caption" color="#991b1b">Version is full — enrollment automatically closed</Typography>
                    )}
                  </Box>
                  <Box>
                    <Typography variant="caption" color="#94a3b8" fontWeight="600" sx={{ textTransform: 'uppercase' }}>Teams</Typography>
                    <Box mt={0.5}>
                      {version.teams_generated || (team && team.length > 0) ? (
                        <Chip
                          label={`Generated · ${version.members_assigned ?? team.filter(m => m.member_id).length} allocated`}
                          size="small"
                          sx={{ bgcolor: '#dcfce7', color: '#166534', fontWeight: 700, borderRadius: '6px' }}
                        />
                      ) : (
                        <Chip
                          label="Not Generated"
                          size="small"
                          sx={{ bgcolor: '#f1f5f9', color: '#64748b', fontWeight: 700, borderRadius: '6px' }}
                        />
                      )}
                    </Box>
                  </Box>
                  {version.description && (
                    <Box>
                      <Typography variant="caption" color="#94a3b8" fontWeight="600" sx={{ textTransform: 'uppercase' }}>Description</Typography>
                      <Typography variant="body2" color="#475569" sx={{ mt: 0.5, lineHeight: 1.6 }}>{version.description}</Typography>
                    </Box>
                  )}
                  <Box>
                    <Typography variant="caption" color="#94a3b8" fontWeight="600" sx={{ textTransform: 'uppercase' }}>Duration</Typography>
                    <Typography variant="body1" fontWeight="700" color="#1e293b">{version.sprint_duration}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="#94a3b8" fontWeight="600" sx={{ textTransform: 'uppercase' }}>Release Target</Typography>
                    <Typography variant="body1" fontWeight="700" color="#1e293b">
                      {version.release_target ? new Date(version.release_target).toLocaleDateString() : 'TBD'}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>

            {/* Schedule & Enrollment Window — Dynamic Version Enrollment */}
            <Card elevation={0} sx={{ borderRadius: '16px', border: '1px solid #e2e8f0' }}>
              <CardContent sx={{ p: 3 }}>
                <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                  <CalendarClock size={18} color="#843FEF" aria-hidden="true" />
                  <Typography variant="h6" fontWeight="700" color="#1e293b">Schedule & Enrollment Window</Typography>
                </Box>
                <Typography variant="caption" color="#94a3b8" sx={{ display: 'block', mb: 2.5 }}>
                  Times are entered in your local timezone ({Intl.DateTimeFormat().resolvedOptions().timeZone}) and stored timezone-aware.
                  Leave a field empty to clear it — versions without dates keep the legacy open/close workflow.
                </Typography>
                {scheduleForm ? (
                  <>
                    <Grid container spacing={1.5}>
                      {SCHEDULE_FIELDS.map(({ key, label }) => (
                        <Grid item xs={12} sm={6} key={key}>
                          <TextField
                            label={label}
                            type="datetime-local"
                            size="small"
                            fullWidth
                            value={scheduleForm[key] || ''}
                            onChange={(e) => {
                              const next = { ...scheduleForm, [key]: e.target.value };
                              setScheduleForm(next);
                              setScheduleErrors(validateScheduleForm(next));
                            }}
                            error={Boolean(scheduleErrors[key])}
                            helperText={scheduleErrors[key] || ' '}
                            InputLabelProps={{ shrink: true }}
                            disabled={!canEditStatus}
                          />
                        </Grid>
                      ))}
                      <Grid item xs={12} sm={6}>
                        <TextField
                          label="Maximum Members"
                          type="number"
                          size="small"
                          fullWidth
                          value={scheduleForm.max_members}
                          onChange={(e) => {
                            const next = { ...scheduleForm, max_members: e.target.value };
                            setScheduleForm(next);
                            setScheduleErrors(validateScheduleForm(next));
                          }}
                          error={Boolean(scheduleErrors.max_members)}
                          helperText={scheduleErrors.max_members || `Empty = default (team size, capped at 25)`}
                          inputProps={{ min: 1 }}
                          disabled={!canEditStatus}
                        />
                      </Grid>
                    </Grid>
                    {typeof data.waitingCount === 'number' && data.waitingCount > 0 && (
                      <Alert severity="info" sx={{ mt: 1.5, borderRadius: '10px' }}>
                        {data.waitingCount} student{data.waitingCount === 1 ? ' is' : 's are'} on this project's waiting list —
                        they are auto-enrolled as soon as a version opens or capacity increases.
                      </Alert>
                    )}
                    <Button
                      fullWidth
                      variant="contained"
                      startIcon={savingSchedule ? <CircularProgress size={16} sx={{ color: 'white' }} /> : <Save size={16} />}
                      disabled={!canEditStatus || savingSchedule || Object.keys(scheduleErrors).length > 0}
                      onClick={handleSaveSchedule}
                      sx={{
                        mt: 2, bgcolor: '#843FEF', '&:hover': { bgcolor: '#7035cc' },
                        borderRadius: '10px', textTransform: 'none', fontWeight: 'bold'
                      }}
                    >
                      {savingSchedule ? 'Saving…' : 'Save Schedule'}
                    </Button>
                    {!canEditStatus && (
                      <Typography variant="caption" color="#94a3b8" sx={{ display: 'block', mt: 1, textAlign: 'center' }}>
                        Admin access required to edit the schedule
                      </Typography>
                    )}
                  </>
                ) : (
                  <CircularProgress size={20} />
                )}
              </CardContent>
            </Card>

            {/* Project Timeline — current stage highlighted, done marked, future muted */}
            <Card elevation={0} sx={{ borderRadius: '16px', border: '1px solid #e2e8f0' }}>
              <CardContent sx={{ p: 3 }}>
                <Box display="flex" alignItems="center" gap={1} mb={2.5}>
                  <Milestone size={18} color="#843FEF" aria-hidden="true" />
                  <Typography variant="h6" fontWeight="700" color="#1e293b">Project Timeline</Typography>
                </Box>
                <ProjectStatusTimeline status={version.status} />
              </CardContent>
            </Card>

            <Card elevation={0} sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', bgcolor: '#843FEF', color: 'white' }}>
              <CardContent sx={{ p: 3 }}>
                <Box display="flex" alignItems="center" gap={2} mb={2}>
                  <Box sx={{ p: 1, borderRadius: '8px', bgcolor: 'rgba(255,255,255,0.2)' }}>
                    <ExternalLink size={20} />
                  </Box>
                  <Typography variant="h6" fontWeight="700">Repository</Typography>
                </Box>
                <Typography variant="body2" sx={{ mb: 2.5, opacity: 0.9 }}>
                  Official GitHub repository for all NextGen projects and versions.
                </Typography>
                <Button 
                  fullWidth 
                  variant="contained" 
                  component="a" 
                  href="https://github.com/evolvexinnovations" 
                  target="_blank"
                  sx={{ 
                    bgcolor: 'white', 
                    color: '#843FEF', 
                    fontWeight: 'bold', 
                    textTransform: 'none',
                    '&:hover': { bgcolor: '#f8fafc' } 
                  }}
                >
                  View GitHub
                </Button>
              </CardContent>
            </Card>
          </Box>
        </Grid>
      </Grid>

      {/* Add Member Dialog */}
      <Dialog open={openAddMember} onClose={() => setOpenAddMember(false)} fullWidth maxWidth="xs">
        <DialogTitle>Add Team Member</DialogTitle>
        <DialogContent dividers>
          <Box display="flex" flexDirection="column" gap={2} pt={1}>
            <TextField 
              select 
              label="Select Member" 
              fullWidth
              value={memberForm.member_id}
              onChange={(e) => setMemberForm({...memberForm, member_id: e.target.value})}
            >
              <MenuItem value="">Unassigned (Role Only)</MenuItem>
              {allMembers.map(m => (
                <MenuItem key={m.id} value={m.id}>{m.full_name} ({m.primary_skill})</MenuItem>
              ))}
            </TextField>
            <TextField 
              select 
              label="Select Role" 
              fullWidth
              value={memberForm.role_id}
              onChange={(e) => setMemberForm({...memberForm, role_id: e.target.value})}
            >
              {availableRoles.map(r => (
                <MenuItem key={r.id} value={r.id}>{r.name}</MenuItem>
              ))}
            </TextField>
            <TextField 
              label="Allocation %" 
              type="number" 
              fullWidth
              value={memberForm.allocation_percentage}
              onChange={(e) => setMemberForm({...memberForm, allocation_percentage: e.target.value})}
            />
            <TextField 
              label="Sprint Assignment" 
              fullWidth
              value={memberForm.sprint_assignment}
              onChange={(e) => setMemberForm({...memberForm, sprint_assignment: e.target.value})}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAddMember(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAddMember} sx={{ bgcolor: '#843FEF' }}>Add Member</Button>
        </DialogActions>
      </Dialog>

      {/* Status editing — popover → confirm → PATCH */}
      <StatusSelectPopover
        anchorEl={statusPopoverAnchor}
        currentStatus={version.status}
        onClose={() => setStatusPopoverAnchor(null)}
        onSelect={handleStatusSelected}
      />
      <StatusConfirmDialog
        open={!!pendingStatus}
        versionName={version.name}
        oldStatus={version.status}
        newStatus={pendingStatus}
        saving={savingStatus}
        onCancel={() => setPendingStatus(null)}
        onConfirm={handleConfirmStatus}
      />
      <StatusHistoryDialog
        open={historyOpen}
        versionId={id}
        versionName={version.name}
        onClose={() => setHistoryOpen(false)}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default VersionDetails;
